// Front-end RBAC for the back-office shell (POC, 2026-06-27).
//
// The portal recognizes three personas, mirroring the TOR "Role" column of the
// POC user sheet: Admin / Maker / Checker & Approver. UMS hands us the role
// strings in `me.roles` (and a derived `me.isAdmin`); we collapse them into one
// of three PortalRoles and drive both the Sidebar (hide menus) and the route
// guard (block direct-URL access) off the same table.
//
// This is POC-grade authorization done in the SPA. The BFF still needs its own
// RBAC before prod — see CLAUDE.md "/api/portal/noti/* … not yet by BFF-level
// RBAC".
import type { AuthUser } from './auth'

export type PortalRole = 'admin' | 'maker' | 'checker'

// Collapse the UMS role strings into one persona. Admin wins (any role matching
// /admin/, same rule the BFF uses for isAdmin); then Checker & Approver; then
// Maker. Anything unrecognized falls back to the most-restricted maker view.
export function portalRole(me: Pick<AuthUser, 'roles' | 'isAdmin'>): PortalRole {
  if (me.isAdmin) return 'admin'
  const blob = me.roles.join(' ').toLowerCase()
  if (/check|approv/.test(blob)) return 'checker'
  if (/maker/.test(blob)) return 'maker'
  return 'maker'
}

export const ROLE_LABEL: Record<PortalRole, string> = {
  admin: 'Admin',
  maker: 'Maker',
  checker: 'Checker & Approver',
}

// Menus each persona must NOT see, matched against the nav `to` / route base.
// `/audit` (Audit Log) is hidden for EVERYONE per the 2026-06-27 POC decision.
export const HIDDEN_NAV: Record<PortalRole, readonly string[]> = {
  admin: ['/audit'],
  maker: ['/audit', '/dashboard', '/transactions', '/api-docs'],
  checker: ['/audit', '/clients', '/workflows', '/channels', '/api-docs'],
}

// Can this persona see a given top-level menu (`to` value, e.g. '/clients')?
export function canSee(role: PortalRole, to: string): boolean {
  return !HIDDEN_NAV[role].includes(to)
}

// First menu the persona is allowed to land on — used for redirects after login
// and when bouncing off a forbidden URL.
const LANDING: Record<PortalRole, string> = {
  admin: '/dashboard',
  maker: '/clients',
  checker: '/dashboard',
}
export function landingFor(role: PortalRole): string {
  return LANDING[role]
}

// Is a full pathname (incl. detail routes like /clients/$id) allowed? We key on
// the first path segment so /clients/abc inherits /clients' permission. The
// index '/' and utility paths are not in any hidden list → allowed.
export function pathAllowed(role: PortalRole, pathname: string): boolean {
  const base = '/' + (pathname.split('/').filter(Boolean)[0] ?? '')
  return canSee(role, base)
}
